import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role_type: 'moderator'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isGovernor = currentUser?.role?.role_type === 'admin' || currentUser?.role?.role_type === 'founder';
  const isFounder = currentUser?.role?.role_type === 'founder';

  const roleLabels = {
    founder: '👑 Fondateur / Gouverneur',
    admin: '🔱 Administrateur',
    deputy_admin: '💼 Administrateur Adjoint',
    moderator: '🛡️ Modérateur',
    account_manager: '💰 Gestionnaire de Comptes'
  };

  const roleDescriptions = {
    founder: 'Gouverneur de la Banque Centrale - Accès total et contrôle complet',
    admin: 'Accès complet à toutes les fonctionnalités de la banque',
    deputy_admin: 'Second en commande, peut gérer les modérateurs et gestionnaires',
    moderator: 'Surveille les transactions et les utilisateurs',
    account_manager: 'Gère les comptes clients et les opérations courantes'
  };

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const { data } = await axios.get(`${API_URL}/admin/staff`, config);
      setStaff(data || []);
    } catch (err) {
      console.error('Erreur chargement staff:', err);
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    const allChars = lowercase + uppercase + numbers + symbols;
    
    let password = '';
    
    // Assurer au moins un caractère de chaque type
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Compléter jusqu'à 18 caractères avec des caractères aléatoires
    for (let i = password.length; i < 18; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Mélanger le mot de passe pour éviter un pattern prévisible
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    return password;
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setFormData({ ...formData, password: newPassword });
    setShowPassword(true); // Afficher le mot de passe généré
  };

  const validatePassword = (password) => {
    if (password.length < 16) {
      return 'Le mot de passe doit contenir au moins 16 caractères';
    }
    if (!/\d/.test(password)) {
      return 'Le mot de passe doit contenir au moins un chiffre';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Le mot de passe doit contenir au moins une majuscule';
    }
    if (!/[a-z]/.test(password)) {
      return 'Le mot de passe doit contenir au moins une minuscule';
    }
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
      return 'Le mot de passe doit contenir au moins un caractère spécial (!@#$%^&*...)';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation du mot de passe
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    // Validation email domaine
    const emailDomain = formData.email.split('@')[1];
    if (emailDomain !== 'bcb.bzh') {
      setError('Les membres du staff doivent avoir une adresse @bcb.bzh');
      return;
    }

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API_URL}/admin/create-staff`, formData, config);
      
      setSuccess(`${roleLabels[formData.role_type]} créé avec succès !`);
      setFormData({
        email: '',
        name: '',
        password: '',
        role_type: 'moderator'
      });
      setShowForm(false);
      loadStaff();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ce membre du staff : ${email} ?`)) {
      return;
    }

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API_URL}/admin/staff/${email}`, config);
      setSuccess('Membre du staff supprimé avec succès');
      loadStaff();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  if (!isGovernor) {
    return (
      <div className="text-center py-20">
        <div className="text-6xl mb-4">🔒</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès Réservé</h2>
        <p className="text-gray-600">Cette page est réservée au Gouverneur de la Banque Centrale de Bretagne.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-20">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">👥 Gestion du Personnel</h1>
          <p className="text-gray-600 mt-1">Nommer et gérer les administrateurs, modérateurs et gestionnaires</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          {showForm ? '✕ Annuler' : '➕ Nommer un membre'}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
          <p className="text-red-800">❌ {error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
          <p className="text-green-800">✅ {success}</p>
        </div>
      )}

      {/* Formulaire de création */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-blue-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Nommer un nouveau membre</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rôle *
              </label>
              <select
                value={formData.role_type}
                onChange={(e) => setFormData({ ...formData, role_type: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                {isFounder && (
                  <option value="founder">👑 Fondateur / Gouverneur (Accès total absolu)</option>
                )}
                <option value="admin">🔱 Administrateur (Accès total)</option>
                <option value="deputy_admin">💼 Administrateur Adjoint</option>
                <option value="moderator">🛡️ Modérateur</option>
                <option value="account_manager">💰 Gestionnaire de Comptes</option>
              </select>
              <p className="text-sm text-gray-600 mt-2">
                {roleDescriptions[formData.role_type]}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom complet *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Jean Dupont"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email (@bcb.bzh) *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="prenom.nom@bcb.bzh"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Seuls les emails @bcb.bzh sont autorisés pour le personnel
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe temporaire *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 pr-24 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Minimum 16 caractères"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 text-gray-600 hover:text-gray-900 text-xl"
                  title={showPassword ? "Masquer" : "Afficher"}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={handleGeneratePassword}
                  className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg text-sm font-medium hover:from-green-600 hover:to-green-700 transition-all shadow-sm"
                >
                  🎲 Générer un mot de passe sécurisé
                </button>
                {formData.password && (
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(formData.password);
                      alert('Mot de passe copié dans le presse-papiers !');
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                  >
                    📋 Copier
                  </button>
                )}
              </div>
              <div className="mt-2 space-y-1 text-xs">
                <p className={formData.password.length >= 16 ? 'text-green-600' : 'text-gray-500'}>
                  {formData.password.length >= 16 ? '✓' : '○'} Au moins 16 caractères
                </p>
                <p className={/[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}>
                  {/[A-Z]/.test(formData.password) ? '✓' : '○'} Au moins une majuscule
                </p>
                <p className={/[a-z]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}>
                  {/[a-z]/.test(formData.password) ? '✓' : '○'} Au moins une minuscule
                </p>
                <p className={/\d/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}>
                  {/\d/.test(formData.password) ? '✓' : '○'} Au moins un chiffre
                </p>
                <p className={/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}>
                  {/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(formData.password) ? '✓' : '○'} Au moins un caractère spécial
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                ✓ Créer le membre
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Liste du personnel */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <h2 className="text-xl font-bold">Personnel Actuel</h2>
          <p className="text-blue-100 text-sm mt-1">{staff.length} membre(s)</p>
        </div>

        <div className="divide-y divide-gray-200">
          {staff.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <div className="text-4xl mb-2">👥</div>
              <p>Aucun membre du personnel pour le moment</p>
            </div>
          ) : (
            staff.map((member, index) => (
              <div key={index} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{roleLabels[member.role]?.split(' ')[0] || '👤'}</span>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
                        <p className="text-sm text-gray-600">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        member.role === 'founder' ? 'bg-yellow-100 text-yellow-900 border-2 border-yellow-400' :
                        member.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                        member.role === 'deputy_admin' ? 'bg-blue-100 text-blue-800' :
                        member.role === 'moderator' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {roleLabels[member.role] || member.role}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      {roleDescriptions[member.role]}
                    </p>
                  </div>

                  {member.email !== currentUser.email && (
                    <button
                      onClick={() => handleDelete(member.email)}
                      className="ml-4 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
                    >
                      🗑️ Révoquer
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Guide de sécurité */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 p-6 rounded-lg">
        <h3 className="font-semibold text-amber-900 mb-3">🔒 Recommandations de Sécurité</h3>
        <ul className="space-y-2 text-sm text-amber-800">
          <li>✓ Les mots de passe doivent maintenant contenir au moins <strong>16 caractères</strong></li>
          <li>✓ Incluez des majuscules, minuscules, chiffres et symboles</li>
          <li>✓ Changez les mots de passe temporaires dès la première connexion</li>
          <li>✓ Ne partagez jamais vos identifiants</li>
          <li>✓ Révoquez immédiatement les accès des membres qui quittent l'organisation</li>
        </ul>
      </div>
    </div>
  );
}

export default StaffManagement;
